import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router-dom";
import PullToRefresh from "react-simple-pull-to-refresh";

import {
  ContainerValue,
  ContainerView,
  FlexBox,
  CorrectPullRefresh,
  PullGrid
} from "components/containers";
import {
  Button,
  TitleH1,
  TitleH18,
  TitleH3,
  TitleH4
} from "components/generic";
import { ButtonsDiv, DivBlock, MainWrapper } from "./styles";
import {
  IMatch,
  IStudentStripeMatches,
  THostShort,
  THouseAddress
} from "interfaces/intarfaces";
import { IcoCalendar } from "components/InputCalendar/styles";
import { LoaderCustom } from "components/Loader";
import { theme } from "styles/theme";
import HowItWork from "components/HowItWork";
import {
  DateLine,
  NoMatchesInfoSection,
  UnauthorizedUser
} from "pages/ProfileUser/SimpleComponents";
import { StudentAPI } from "API/studentApi";
import { UserAPI } from "API/userApi";
import {
  MatchStatusEnum,
  ROOM_INFO_ROUTE,
  STUDENT_CHATS_ROUTE_ID
} from "utils/constants";
import { useContextHospi } from "context/ContextHospi";
import { getNameCountry } from "utils/fetchMapInfo";
import { HelpWidget } from "components/HelpWidget";
import { HouseAPI } from "API/houseApi";
import { sortMatches } from "utils/helpers";
import { TLang } from "assets/data/isoNameLanguage";
import { useChat } from "../../context/ContextChat/ContextChat";

const StudentMatches: React.FC = () => {
  const [listOffers, setListOffers] = useState<IStudentStripeMatches[]>([]);
  const { t } = useTranslation("matches");
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const { userProfile } = useContextHospi();
  const [response401, setResponse401] = useState(false);

  const nameProps = (status: number, mode: "in" | "out"): keyof IMatch =>
    status === MatchStatusEnum.INVITED
      ? `offered_check_${mode}_date`
      : `check_${mode}_date`;

  const fetchData = () => {
    setLoading(true);
    StudentAPI.getMatches(userProfile?.user_id!)
      .then((response) => {
        let hostCache = new Map<string, THostShort>();
        let houseCache = new Map<string, THouseAddress>();
        const arrayHostID: string[] = [];
        const arrayHouseID: string[] = [];
        response.forEach((item: IMatch) => {
          !arrayHostID.includes(item.host_id) && arrayHostID.push(item.host_id);
          !arrayHouseID.includes(item.house_id) &&
            arrayHouseID.push(item.house_id);
        });
        Promise.allSettled([
          ...arrayHostID.map(
            (item) => UserAPI.getUserPreview(item) as Promise<THostShort>
          ),
          ...arrayHouseID.map((item) => HouseAPI.getHouse(item))
        ]).then((results) => {
          results.forEach(
            (
              item: PromiseSettledResult<THostShort | THouseAddress>,
              num: number
            ) => {
              item.status === "fulfilled" &&
                (num < arrayHostID.length
                  ? hostCache.set(
                    (item.value as THostShort).user_id,
                    item.value as THostShort
                  )
                  : houseCache.set(
                    (item.value as THouseAddress).house_id!,
                    item.value as THouseAddress
                  ));
            }
          );
          const tempData: IStudentStripeMatches[] = [];

          response.forEach((item: IMatch, num: number) => {
            if (
              hostCache.get(item.host_id) !== undefined &&
              houseCache.get(item.house_id) !== undefined
            )
              if (response[num].match_status! > MatchStatusEnum.CHATTED)
                tempData.push({
                  host: hostCache.get(item.host_id)!,
                  address: houseCache.get(item.house_id)!,
                  period: {
                    start:
                      (response[num][
                        nameProps(response[num].match_status!, "in")
                      ] as string) ?? userProfile?.preferred_move_in,
                    end:
                      (response[num][
                        nameProps(response[num].match_status!, "out")
                      ] as string) ?? userProfile?.preferred_move_out,
                    currently:
                      response[num].match_status! > MatchStatusEnum.INVITED
                  }
                });
          });
          setListOffers(() => tempData.sort(sortMatches));
          setLoading(false);
          setDataLoaded(true);
        });
        setLoading(false);
      })
      .catch(() => {
        setListOffers([]);
        setLoading(false);
        setDataLoaded(true);
      });
  };
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [userProfile, dataLoaded]);

  const handleOffer = (
    offer_data: Pick<IMatch, "house_id" | "host_id">,
    accept: boolean
  ) => {
    setLoading(true);
    (accept
      ? StudentAPI.acceptInvite({
        ...offer_data,
        student_id: userProfile?.user_id || ""
      })
      : StudentAPI.rejectInvite({
        ...offer_data,
        student_id: userProfile?.user_id || ""
      })
    )
      .then(() => {
        if (accept) {
          setListOffers((prev) =>
            prev.map((item) => {
              if (item.address.house_id === offer_data.house_id)
                return {
                  ...item,
                  period: {
                    ...item.period,
                    currently: accept
                  }
                };
              return item;
            })
          );
        } else {
          setListOffers((prev) =>
            prev.filter((item) => item.address.house_id !== offer_data.house_id)
          );
        }
      })
      .catch((e) => {
        setResponse401(e?.response?.status === 401);
      })
      .finally(() => setLoading(false));
  };

  // When user Unauthorized
  if (response401) return <UnauthorizedUser />;

  // console.log(listOffers)
  const isCurrentlyLiving = (start: string, end: string) => {
    const currentDate = new Date();
    const startDate = new Date(start);
    const endDate = new Date(end);

    return currentDate >= startDate && currentDate <= endDate;
  };

  return (
    <ContainerView>
      <MainWrapper style={{ paddingBottom: "1rem" }}>
        {loading && (
          <LoaderCustom
            size={5}
            color={theme.colors.bgBody}
            text={t("loading")}
          />
        )}
        <CorrectPullRefresh>
          <PullToRefresh
            onRefresh={async () => {
              fetchData();
              setDataLoaded(false);
            }}
          >
            <PullGrid>
              <TitleH1>{t("student.title")}</TitleH1>
              <HowItWork
                title={t("butHowItWork")}
                items={[t("student.firstDesc")]}
              />
              {dataLoaded && !listOffers.length && <NoMatchesInfoSection />}
              {listOffers.length > 0 && (
                <>
                  {listOffers.some((item) => isCurrentlyLiving(item.period.start, item.period.end)) && (
                    <>
                      <TitleH3 style={{ justifySelf: "flex-start" }}>
                        {t("student.livingHere")}
                      </TitleH3>
                      {listOffers
                        .filter((item) => isCurrentlyLiving(item.period.start, item.period.end))
                        .map((item: IStudentStripeMatches, i: number) => (
                          <MatchCardStudent {...item} key={i} />
                        ))}
                    </>
                  )}
                  {listOffers.some((item) => new Date(item.period.start) > new Date()) && (
                    <>
                      <TitleH3 style={{ justifySelf: "flex-start" }}>
                        {t("student.futureLiving")}
                      </TitleH3>
                      {listOffers
                        .filter((item) => new Date(item.period.start) > new Date())
                        .map((item: IStudentStripeMatches, i: number) => (
                          <MatchCardStudent {...item} key={i} />
                        ))}
                    </>
                  )}
                  {listOffers.some((item) => new Date(item.period.end) < new Date()) && (
                    <>
                      <TitleH3 style={{ justifySelf: "flex-start" }}>
                        {t("student.pastLiving")}
                      </TitleH3>
                      {listOffers
                        .filter((item) => new Date(item.period.end) < new Date())
                        .map((item: IStudentStripeMatches, i: number) => (
                          <MatchCardStudent {...item} key={i} />
                        ))}
                    </>
                  )}
                </>
              )}
              <HelpWidget
                title={t(`student.help.title`)}
                text={[t(`student.help.text`)]}
                btnText={t(`student.help.btnText`)}
              />
            </PullGrid>
          </PullToRefresh>
        </CorrectPullRefresh>
      </MainWrapper>
    </ContainerView>
  );
};

export default StudentMatches;

const MatchCardStudent: React.FC<
  IStudentStripeMatches & {
    handleOffer?: (
      offer_data: Pick<IMatch, "house_id" | "host_id">,
      accept: boolean
    ) => void;
  }
> = ({ host, address, period, handleOffer }) => {
  const {
    state: { chats }
  } = useChat();

  const navigate = useNavigate();
  const { t, i18n } = useTranslation("matches");

  const handleInvite = (accept: boolean) => {
    handleOffer &&
      handleOffer(
        {
          host_id: host.user_id,
          house_id: address.house_id!
        },
        accept
      );
  };

  const houseChat = chats.find((c) => {
    return c.house_id === address.house_id;
  });

  const currLang = i18n.language.toUpperCase() as TLang;
  return (
    <ContainerValue style={{ padding: "1rem", margin: 0 }}>
      {!period.currently && (
        <>
          <TitleH3>{`${host.first_name} ${host.last_name} ${t(
            "student.invitation"
          )}`}</TitleH3>
          <TitleH4 m="0 0 8px 0">{t("student.secondDesc")}</TitleH4>
        </>
      )}
      <FlexBox justify="flex-start" align="center">
        <FlexBox justify="flex-start">
          <IcoCalendar
            size="1.1em"
            fontColor
            style={{ margin: "0 8px 0 0", cursor: "default" }}
          />
          <DateLine start={period.start} end={period.end} />
        </FlexBox>
        {!period.currently && (
          <div className="buttonGroup">
            <Button normal m="0 16px 0 0" onClick={() => handleInvite(false)}>
              {t("student.butDecline")}
            </Button>
            <Button main onClick={() => handleInvite(true)}>
              {t("student.butAccept")}
            </Button>
          </div>
        )}
      </FlexBox>
      <hr />
      <FlexBox justify="flex-start" align="flex-start">
        <img
          src={host.thumbnail_url + `?${new Date().toString()}`}
          alt="Profile"
          style={{ paddingRight: "16px" }}
        />
        <DivBlock>
          <TitleH18>{`${host.first_name} ${host.last_name}`}</TitleH18>
          <TitleH3>{`${address.street} ${address.house_number} ${address.additional_house_number}`}</TitleH3>
          <TitleH4>{`${address.postal_code} ${address.province}`}</TitleH4>
          <TitleH4>{`${getNameCountry(address.country, currLang)}`}</TitleH4>
        </DivBlock>
      </FlexBox>
      <ButtonsDiv>
        <Button
          outline
          onClick={() =>
            navigate(
              `/${i18n.language}${ROOM_INFO_ROUTE.slice(5)}` +
              `/${address.house_id}`
            )
          }
        >
          {t("student.butInfo")}
        </Button>
        {houseChat ? (
          <Button
            normal
            onClick={() => {
              navigate(
                generatePath(STUDENT_CHATS_ROUTE_ID, {
                  lng: i18n.language,
                  id: houseChat.chat_id
                })
              );
            }}
          >
            {t("student.butChat")}
          </Button>
        ) : null}
      </ButtonsDiv>
    </ContainerValue>
  );
};
